import { apiClient } from './client';
import { createCrudApi } from './crud';
import type { ApiSuccess } from '../types';

export type OmsStatus = 'draft' | 'pending_approval' | 'active' | 'on_hold' | 'completed' | 'closed' | 'cancelled';
export type OmsAction = 'submit' | 'approve' | 'reject' | 'hold' | 'resume' | 'complete' | 'close' | 'cancel';

export interface Oms {
  id: number;
  omsNumber: string;
  title: string | null;
  description: string | null;
  clientId: number | null;
  projectId: number | null;
  siteId: number | null;
  status: OmsStatus;
  isLocked: boolean;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedBy: number | null;
  createdAt: string;
}

export interface CreateOmsInput {
  omsNumber: string;
  title: string;
  description?: string;
  clientId: number;
  projectId: number;
  siteId?: number | null;
}

export interface OmsLink { id: number; txnType: string; txnRef: string | null; txnId: number | null; createdAt: string; }
export interface OmsDocument { id: number; docType: string; fileName: string; fileUrl: string; version: number; isCurrent: boolean; createdAt: string; }
export interface TimelineEvent { type: 'status' | 'activity' | 'link'; at: string; action: string; from?: string | null; to?: string; remarks?: string | null; txnType?: string; txnRef?: string | null; actorUserId?: number | null; }
export interface OmsDashboard { total: number; byStatus: Record<OmsStatus, number>; }

const base = createCrudApi<Oms, CreateOmsInput, Partial<CreateOmsInput>>('oms');

export const omsApi = {
  ...base,
  async dashboard(): Promise<OmsDashboard> {
    const { data } = await apiClient.get<ApiSuccess<OmsDashboard>>('/oms/dashboard');
    return data.data;
  },
  async changeStatus(id: number, action: OmsAction, remarks?: string): Promise<Oms> {
    const { data } = await apiClient.post<ApiSuccess<Oms>>(`/oms/${id}/status`, { action, remarks });
    return data.data;
  },
  async timeline(id: number): Promise<TimelineEvent[]> {
    const { data } = await apiClient.get<ApiSuccess<TimelineEvent[]>>(`/oms/${id}/timeline`);
    return data.data;
  },
  async listLinks(id: number): Promise<OmsLink[]> {
    const { data } = await apiClient.get<ApiSuccess<OmsLink[]>>(`/oms/${id}/links`);
    return data.data;
  },
  async listDocuments(id: number): Promise<OmsDocument[]> {
    const { data } = await apiClient.get<ApiSuccess<OmsDocument[]>>(`/oms/${id}/documents`);
    return data.data;
  },
  async addDocument(id: number, input: { docType: string; fileName: string; fileUrl: string }): Promise<OmsDocument> {
    const { data } = await apiClient.post<ApiSuccess<OmsDocument>>(`/oms/${id}/documents`, input);
    return data.data;
  },
};

/** Actions available from each status (mirrors the backend workflow). */
export const STATUS_ACTIONS: Record<OmsStatus, OmsAction[]> = {
  draft: ['submit', 'cancel'],
  pending_approval: ['approve', 'reject', 'cancel'],
  active: ['hold', 'complete', 'cancel'],
  on_hold: ['resume', 'cancel'],
  completed: ['close'],
  closed: [],
  cancelled: [],
};
